import React, { useState, useEffect, useReducer, Fragment, useMemo } from 'react'
import { Flex, Box } from 'reflexbox/styled-components'
import { toast } from 'react-toastify'
import { useHistory, useParams } from 'react-router-dom'
import moment from 'moment'

import { Formik, Form } from 'formik'

import Button from 'components/Button'
import Card from 'components/Card'
import Breadcrumb from 'components/Breadcrumb'
import Loader from 'components/Loader'
import DataListingTable from 'components/DataListingTable'
import { DeleteModal } from 'components/Modal'
import { Title, SemiTitle, Text } from 'components/Typography'
import { TextField, DateRangePickerField } from 'components/Form'

import { download } from 'helpers/download'
import { getCouponDetail, deleteDiscountCoupon, getCouponUseHistory, exportClientsCSV } from 'services/coupons'
import { mapCouponFromJSON } from 'helpers/mappers'
import { formatCurrency } from 'utils/formatters'

const reducer = (deleteFeedback, action) => {
  switch (action.type) {
    case 'isLoading':
      return { status: 'isLoading' }
    case 'successfulDeletion':
      return { status: 'successfulDeletion' }
    case 'deletionFailed':
      return { status: 'deletionFailed' }
  }
}

const tableColumns = [
  {
    Header: 'Email do assinante',
    accessor: 'subscriber_email',
  },
  {
    Header: 'Utilização',
    accessor: 'used_date',
  },
]

const Show = () => {
  const { id: couponID } = useParams()
  const [coupon, setCoupon] = useState(null)
  const [couponUsedHistory, setCouponUsedHistory] = useState([])
  const [deleteModalIsOpened, setDeleteModalIsOpened] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [subscribersTableLoading, setSubscribersTableLoading] = useState(false)
  const [sortBy, setSortBy] = useState(null)
  const [filters, setFilters] = useState({})
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalRecords: 0,
  })

  const [state, dispatch] = useReducer(reducer, { status: '' })
  const history = useHistory()

  const breadcrumbPath = useMemo(
    () => [
      {
        label: 'Cupons',
        link: '/staff/promotions/coupons',
      },
      {
        label: coupon?.code,
      },
    ],
    [coupon],
  )

  const handleTableDataFetch = ({ currentPage, pageSize, sortBy }) => {
    setPagination({ ...pagination, currentPage, pageSize })
    setSortBy(sortBy)
  }

  const deleteRequest = async () => {
    try {
      dispatch({ type: 'isLoading' })
      await deleteDiscountCoupon(couponID)
      dispatch({ type: 'successfulDeletion' })
      toast.success(`Cupom ${coupon.code} deletado com sucesso!`)
      history.push('/staff/promotions/coupons')
    } catch (e) {
      console.log(e)
      dispatch({ type: 'deletionFailed' })
    }
  }

  const getCsv = async () => {
    try {
      const { data } = await exportClientsCSV(couponID)

      download(data, coupon.code)
    } catch (e) {
      toast.error('Erro ao fazer o download do arquivo CSV.')
      console.log(e)
    }
  }

  const handleFilterSubcribersSubmit = ({ search, period }) => {
    setFilters({
      ...(search && { email: search }),
      ...(period?.startDate && { first_date: moment(period.startDate._d).format('YYYY-MM-DD') }),
      ...(period?.endDate && { last_date: moment(period.endDate._d).format('YYYY-MM-DD') }),
    })
  }

  useEffect(() => {
    const fetchCoupon = async () => {
      try {
        setIsLoading(true)
        const { data } = await getCouponDetail(couponID)

        setCoupon(mapCouponFromJSON(data))
      } catch (e) {
        console.log(e)
        toast.error('Errou ao carregar informações do cupom, tente novamente.')
      } finally {
        setIsLoading(false)
      }
    }

    fetchCoupon()
  }, [])

  useEffect(() => {
    const fetchCouponUsedHistory = async () => {
      try {
        setSubscribersTableLoading(true)
        const data = await getCouponUseHistory(couponID, { sortBy, pagination, ...filters })

        const formattedData = data.subscribers.map(item => ({
          subscriber_email: item.subscriber_email || '-',
          used_date: `${moment(item.used_date).format('DD/MM/YYYY')}`,
        }))

        setPagination(data.pagination)
        setCouponUsedHistory(formattedData)
      } catch (e) {
        console.log(e)
        toast.error('Erro ao carregar dados dos Assinantes!')
      } finally {
        setSubscribersTableLoading(false)
      }
    }

    if (sortBy) {
      fetchCouponUsedHistory()
    }
  }, [pagination.currentPage, pagination.pageSize, filters, sortBy])

  if (isLoading)
    return (
      <Flex justifyContent='center' alignItems='center' minHeight='80vh'>
        <Loader />
      </Flex>
    )

  return (
    <Flex flexDirection='column'>
      <Breadcrumb children={breadcrumbPath} />
      <Flex mb='24px' justifyContent='space-between' alignItems='center'>
        <Title as='h3'>{coupon?.code}</Title>
        <Box>
          <Button onClick={() => history.push(`/staff/promotions/coupons/${couponID}/edit`)} mr={12}>
            Editar
          </Button>
          <Button outlined onClick={() => setDeleteModalIsOpened(true)}>
            Excluir
          </Button>
        </Box>
      </Flex>

      <Card mb={32}>
        <Box mb='10px'>
          <SemiTitle color='grays.N500'>Status</SemiTitle>
          <Text>{coupon?.active ? 'Ativo' : 'Inativo'}</Text>
        </Box>

        <Box mb='10px'>
          <SemiTitle color='grays.N500'>Descrição</SemiTitle>
          <Text>{coupon?.description}</Text>
        </Box>

        <Box mb='10px'>
          <SemiTitle color={'grays.N500'}>Cupom limitado a uma lista de clientes</SemiTitle>
          {coupon?.specific_users_only ? (
            <>
              <Text>Sim </Text>
              <Button
                variant='ghost'
                onClick={getCsv}
                color='turquoises.T300'
                size='smallest'
                style={{ textDecoration: 'underline' }}
                fontWeight='normal'
                fontSize='16px'
              >
                Arquivo.CSV
              </Button>
            </>
          ) : (
            <Text>Não</Text>
          )}
        </Box>

        <Box mb='10px'>
          <SemiTitle color='grays.N500'>Código do Cupom</SemiTitle>
          <Text>{coupon?.code}</Text>
        </Box>

        <Box mb='10px'>
          <SemiTitle color='grays.N500'>Valor</SemiTitle>
          <Text>{formatCurrency(coupon?.value)}</Text>
        </Box>

        <Box mb='10px'>
          <SemiTitle color='grays.N500'>Data de validade</SemiTitle>
          <Text>
            {moment(coupon?.initial_date).format('DD/MM/YYYY')} - {moment(coupon?.expiration_date).format('DD/MM/YYYY')}
          </Text>
        </Box>
      </Card>
      <DeleteModal
        isOpened={deleteModalIsOpened}
        setIsOpened={state => setDeleteModalIsOpened(state)}
        title='Excluir cupom'
        text='Tem certeza que deseja excluir este cupom?'
        onDelete={deleteRequest}
        status={state?.status}
      />

      <Title as='h3'>Assinantes</Title>

      <Formik
        onSubmit={handleFilterSubcribersSubmit}
        initialValues={{ search: '', period: { startData: undefined, endDate: undefined } }}
      >
        <Flex as={Form} my={24}>
          <TextField name='search' label='Pesquisar' placeholder='Pesquisar' mr={24} />
          <DateRangePickerField name='period' label='Período' mr={24} />
          <Button variant='secondary'>Filtrar</Button>
        </Flex>
      </Formik>

      <DataListingTable
        data={couponUsedHistory}
        columns={tableColumns}
        fetchData={handleTableDataFetch}
        loading={subscribersTableLoading}
        pagination={pagination}
      />
    </Flex>
  )
}

export default Show
